import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Section from "./layouts/Section";
import PageLayout from "./layouts/PageLayout";
import emailjs from "@emailjs/browser";
import { trackEvent } from "../utils/tracking";

function ContactUs() {
  const form = useRef();
  const nameRef = useRef();
  const emailRef = useRef();
  const deviceRef = useRef();
  const subjectRef = useRef();
  const messageRef = useRef();

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  // Prefill from URL params (e.g., ?device=iPhone&service=Screen+Repair from the Instant Quote widget)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const device = params.get("device");
    const service = params.get("service");
    if (device && deviceRef.current) deviceRef.current.value = device;
    if (service && subjectRef.current) subjectRef.current.value = service;
    // Scroll to form if hash present
    if (location.hash === "#contact-form") {
      setTimeout(() => {
        const el = document.getElementById("contact-form");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [location]);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    emailjs
      .sendForm("service_f4grfuj", "template_bjg4ob4", form.current, {
        publicKey: "LyFrQSP0J8xcnoKSH",
      })
      .then(
        () => {
          // Fire conversion tracking (FB Pixel + Google Ads + GA4)
          trackEvent.leadSubmit({
            device: deviceRef.current?.value,
            service: subjectRef.current?.value,
          });
          setStatus({
            type: 'success',
            message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
          });
          // Reset form fields
          nameRef.current.value = "";
          emailRef.current.value = "";
          deviceRef.current.value = "";
          subjectRef.current.value = "";
          messageRef.current.value = "";
          setIsSubmitting(false);
        },
        (error) => {
          setStatus({
            type: 'error',
            message: 'Oops! Something went wrong. Please try again or call us directly at (215) 939-3933.'
          });
          setIsSubmitting(false);
          console.log("FAILED...", error?.text);
        }
      );
  };

  return (
    <PageLayout
      title="Contact Cell Station | Free Repair Quote in Willow Grove, PA"
      description="Get a free quote for phone, tablet, or laptop repair. Same-day service in Willow Grove, PA. Call (215) 939-3933 or submit our quick form."
      url="https://www.icellstation.com/contact-us"
    >
    <Section title={"Contact Us"}>
      <section className="section page-content">
        <div className="container">
          <div className="row">
            {/* Contact Info Cards */}
            <div className="col-lg-4 mb-4">
              <div style={{
                background: '#f8f9fa',
                padding: '30px',
                borderRadius: '12px',
                height: '100%'
              }}>
                <h4 className="text-dark mb-20">Contact Information</h4>

                <div className="mb-20">
                  <div className="d-flex align-items-center mb-15">
                    <span className="fas fa-phone-alt text-primary mr-3" style={{ fontSize: '1.2rem' }} />
                    <div>
                      <strong>Phone</strong><br />
                      <a href="tel:+12159393933" style={{ color: '#4a4a4a' }}>(215) 939-3933</a>
                    </div>
                  </div>

                  <div className="d-flex align-items-center mb-15">
                    <span className="fas fa-envelope text-primary mr-3" style={{ fontSize: '1.2rem' }} />
                    <div>
                      <strong>Email</strong><br />
                      <a href="mailto:icellstation1@gmail.com" style={{ color: '#4a4a4a' }}>icellstation1@gmail.com</a>
                    </div>
                  </div>

                  <div className="d-flex align-items-center mb-15">
                    <span className="fas fa-map-marker-alt text-primary mr-3" style={{ fontSize: '1.2rem' }} />
                    <div>
                      <strong>Address</strong><br />
                      <span style={{ color: '#4a4a4a' }}>2500 W Moreland Rd, Space #1077<br />Willow Grove, PA 19090</span>
                    </div>
                  </div>

                  <div className="d-flex align-items-center">
                    <span className="fas fa-clock text-primary mr-3" style={{ fontSize: '1.2rem' }} />
                    <div>
                      <strong>Hours</strong><br />
                      <span style={{ color: '#4a4a4a' }}>Mon-Thu: 10AM-8PM<br />Fri-Sat: 10AM-9PM<br />Sun: 10AM-8PM</span>
                    </div>
                  </div>
                </div>

                {/* Quick Call CTA */}
                <a
                  href="tel:+12159393933"
                  className="btn btn-primary w-100"
                  style={{ padding: '12px 20px' }}
                >
                  <i className="fas fa-phone-alt mr-2" />
                  Call Now
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-8" id="contact-form">
              <div style={{
                background: '#ffffff',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                <h1 className="text-dark mb-10" style={{ fontSize: "1.6rem" }}>Request a Quote or Book a Repair</h1>
                <p className="text-muted mb-25">Tell us about your device and we'll get back to you with a free estimate - usually within 1 hour!</p>

                {/* Status Messages */}
                {status.message && (
                  <div
                    style={{
                      padding: '15px 20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      background: status.type === 'success' ? '#d4edda' : '#f8d7da',
                      color: status.type === 'success' ? '#155724' : '#721c24',
                      border: `1px solid ${status.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                    }}
                  >
                    <i className={`fas ${status.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`} />
                    {status.message}
                  </div>
                )}

                <form ref={form} onSubmit={sendEmail}>
                  <div className="row">
                    <div className="form-group mb-20 col-md-6">
                      <label htmlFor="name" className="mb-2 font-w-600">Name *</label>
                      <input
                        id="name"
                        name="name"
                        className="form-control py-3 px-3"
                        type="text"
                        placeholder="Your name"
                        required
                        ref={nameRef}
                      />
                    </div>
                    <div className="form-group mb-20 col-md-6">
                      <label htmlFor="email" className="mb-2 font-w-600">Email *</label>
                      <input
                        id="email"
                        name="email"
                        className="form-control py-3 px-3"
                        type="email"
                        placeholder="Your email"
                        required
                        ref={emailRef}
                      />
                    </div>
                    <div className="form-group mb-20 col-md-6">
                      <label htmlFor="device" className="mb-2 font-w-600">Device Type *</label>
                      <select
                        id="device"
                        name="device"
                        className="form-control py-3 px-3"
                        required
                        ref={deviceRef}
                        defaultValue=""
                      >
                        <option value="" disabled>Select your device</option>
                        <option value="iPhone">iPhone</option>
                        <option value="Samsung">Samsung</option>
                        <option value="iPad">iPad / Tablet</option>
                        <option value="MacBook">MacBook</option>
                        <option value="Laptop">Windows Laptop</option>
                        <option value="Desktop">Desktop Computer</option>
                        <option value="Other">Other Device</option>
                      </select>
                    </div>
                    <div className="form-group mb-20 col-md-6">
                      <label htmlFor="service" className="mb-2 font-w-600">Service Needed *</label>
                      <select
                        id="service"
                        name="subject"
                        className="form-control py-3 px-3"
                        required
                        ref={subjectRef}
                        defaultValue=""
                      >
                        <option value="" disabled>Select a service</option>
                        <option value="Screen Repair">Screen Repair</option>
                        <option value="Battery Replacement">Battery Replacement</option>
                        <option value="Charging Port Repair">Charging Port Repair</option>
                        <option value="Water Damage">Water Damage Repair</option>
                        <option value="Data Recovery">Data Recovery</option>
                        <option value="Software Issue">Software Issue</option>
                        <option value="Quote Request">Get a Quote</option>
                        <option value="Other">Other Service</option>
                      </select>
                    </div>
                    <div className="form-group mb-20 col-md-12">
                      <label htmlFor="message" className="mb-2 font-w-600">Message *</label>
                      <textarea
                        id="message"
                        className="form-control p-3"
                        name="message"
                        required
                        rows="5"
                        ref={messageRef}
                        placeholder="Tell us about your device issue or question..."
                      />
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={isSubmitting}
                    style={{ padding: '14px 35px' }}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Section>
    </PageLayout>
  );
}

export default ContactUs;
